// Thin client for the server-side render API. All rendering happens on the
// backend (ffmpeg) -- this module just uploads files, kicks off a job, and
// polls it. Uploads are chunked so large source files (long WAV masters,
// high-res photos) never depend on a single oversized request surviving the
// proxy in front of the app.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UPLOAD_RETRIES = 3;
const UPLOAD_CONCURRENCY = 3;

function statusHint(status) {
  if (status === 413) return 'That file was too large for a single upload step. Please try again.';
  if (status === 429) return 'Too many requests right now. Please wait a moment and try again.';
  if (status === 502 || status === 503 || status === 504) {
    return 'The server was unreachable or took too long. Please check your connection and try again.';
  }
  if (status >= 500) return 'The server hit an unexpected error. Please try again.';
  return `Request failed (${status}).`;
}

async function parseErrorMessage(res) {
  try {
    const data = await res.json();
    return data.detail || data.message || statusHint(res.status);
  } catch (e) {
    return statusHint(res.status);
  }
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return res.json();
}

async function putChunk(url, blob) {
  let lastError = null;
  for (let attempt = 0; attempt < UPLOAD_RETRIES; attempt += 1) {
    let res = null;
    try {
      res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      });
    } catch (e) {
      lastError = new Error('The connection dropped while uploading. Please check your internet and try again.');
    }
    if (res) {
      if (res.ok) return res.json();
      const message = await parseErrorMessage(res);
      // Anything but a timeout/rate-limit/server error is final.
      if (res.status < 500 && res.status !== 408 && res.status !== 429) throw new Error(message);
      lastError = new Error(message);
    }
    await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
  }
  throw lastError || new Error('Upload failed. Please try again.');
}

async function uploadChunked(file, kind, onProgress) {
  const { upload_id, chunk_size } = await postJson(`${API}/uploads/init`, {
    kind,
    filename: file.name,
    size_bytes: file.size,
    content_type: file.type || null,
  });

  const offsets = [];
  for (let offset = 0; offset < file.size; offset += chunk_size) offsets.push(offset);

  // Several chunks in flight at once: the server keys each chunk by its
  // offset, so order does not matter and a big master uploads far faster.
  let sent = 0;
  let next = 0;
  const worker = async () => {
    while (next < offsets.length) {
      const offset = offsets[next];
      next += 1;
      const end = Math.min(offset + chunk_size, file.size);
      await putChunk(`${API}/uploads/${upload_id}/chunk?offset=${offset}`, file.slice(offset, end));
      sent += end - offset;
      if (onProgress) onProgress(sent / file.size);
    }
  };
  await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, offsets.length) }, worker));

  const res = await fetch(`${API}/uploads/${upload_id}/complete`, { method: 'POST' });
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return res.json();
}

export function uploadImage(file, onProgress) {
  return uploadChunked(file, 'image', onProgress); // { file_id, width, height }
}

export function uploadAudio(file, onProgress) {
  return uploadChunked(file, 'audio', onProgress); // { file_id, duration }
}

// Same chunked pipe as uploadAudio -- the "audio" slot also accepts video
// files so the backend can pull out just their soundtrack. Named separately
// here for readability at call sites that are extracting audio from video.
export const uploadVideoForAudioExtraction = uploadAudio;

export async function fetchUploadedAudio(fileId) {
  const res = await fetch(`${API}/uploads/${fileId}/file`);
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return res.blob();
}

export async function createRender(payload) {
  return postJson(`${API}/render`, payload); // { job_id }
}

export async function getRenderStatus(jobId) {
  const res = await fetch(`${API}/render/${jobId}`);
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return res.json(); // { status, progress, phase, error, download_url }
}

export async function downloadRenderBlob(jobId) {
  const res = await fetch(`${API}/render/${jobId}/file`);
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return res.blob();
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function pollRenderJob(jobId, { onProgress, intervalMs = 1500, timeoutMs = 12 * 60 * 1000 } = {}) {
  const start = Date.now();
  let consecutiveFailures = 0;
  while (true) {
    try {
      const status = await getRenderStatus(jobId);
      consecutiveFailures = 0;
      if (onProgress) onProgress(status);
      if (status.status === 'done' || status.status === 'error') return status;
    } catch (e) {
      consecutiveFailures += 1;
      if (consecutiveFailures >= 5) {
        throw new Error('Lost contact with the render server. Please check your connection and try again.');
      }
    }
    if (Date.now() - start > timeoutMs) {
      throw new Error('This render is taking unusually long. Try a shorter trim and export again.');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
